<script setup lang="ts">
import { instance as api } from '@/helpers/api'
// import link from '@/components/link.vue';
import AdminTools from '@/components/admin-tools.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { TgWappenSvg, ShWappenSvg } from '@/helpers/images'
import { globalVar, useAuthUser } from '@/helpers/globals'
import { onMounted, ref } from 'vue'

const gesucheCount = ref(0)
const toSubmitCount = ref(0)

onMounted(() => {
    loadGesucheCount()
})

const webroot = globalVar('webroot')
const customer = globalVar('customer')
const benutzer = useAuthUser()

async function loadGesucheCount() {
    let res = await api().get('/gesuch/count', {
        params: {
            status: 'open',
        },
    })
    if (res?.data?.count) {
        gesucheCount.value = res.data.count
    } else {
        gesucheCount.value = 0
    }

    res = await api().get('/gesuch/count', {
        params: {
            status: 'notCommited',
        },
    })
    if (res?.data?.count) {
        toSubmitCount.value = res.data.count
    } else {
        toSubmitCount.value = 0
    }
}
function onDownloadClick(filename: string) {
    api().get('/downloadcount/add', {
        params: {
            filename: filename,
        },
    })
}
</script>

<template>
    <div>
        <KpToolbar
            :title="
                $t('welcome_word') +
                ' ' +
                (benutzer?.profilAdresse?.vorname
                    ? benutzer?.profilAdresse.vorname + ' ' + benutzer?.profilAdresse.nachname
                    : benutzer?.profilAdresse?.organisation_name || benutzer?.email)
            "
        >
        </KpToolbar>
        <div class="centered-container">
            <v-container fluid>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-card :to="{ name: 'gesuche' }" hover>
                            <template v-slot:prepend>
                                <v-avatar>
                                    <v-icon>fas fa-balance-scale</v-icon>
                                </v-avatar>
                            </template>
                            <template v-slot:title>
                                {{ $t('gesuche') }}
                            </template>
                            <template v-slot:append>
                                <v-chip
                                    v-if="gesucheCount > 0"
                                    class="hidden-xs"
                                    color="info"
                                    variant="flat"
                                >
                                    <v-avatar color="info">
                                        {{ gesucheCount }}
                                    </v-avatar>
                                    {{ $t('open') }}
                                    <span v-if="toSubmitCount > 0" style="margin-left: 1em">
                                        ({{ $t('not_submitted') }}: {{ toSubmitCount }})
                                    </span>
                                </v-chip>
                            </template>
                            <v-chip
                                v-if="gesucheCount > 0"
                                class="hidden-sm-and-up ml-2 mb-2"
                                color="info"
                                variant="flat"
                            >
                                <v-avatar color="info">
                                    {{ gesucheCount }}
                                </v-avatar>
                                {{ $t('open') }}
                                <span v-if="toSubmitCount > 0" style="margin-left: 1em">
                                    ({{ $t('not_submitted') }}: {{ toSubmitCount }})
                                </span>
                            </v-chip>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card to="/gesuche/erstellen" hover color="action">
                            <template v-slot:prepend>
                                <v-avatar>
                                    <v-icon :color="customer === 'tg' ? 'yellow darken-3' : '#333'"
                                        >fas fa-star</v-icon
                                    >
                                </v-avatar>
                            </template>
                            <template v-slot:title>
                                {{ $t('create_gesuch') }}
                            </template>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-card>
                            <v-card-title>
                                <span>{{ $t('anleitungen') }}</span>
                            </v-card-title>
                            <v-card-text>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            class="rounded mr-2"
                                            variant="outlined"
                                            v-bind="props"
                                            color="primary"
                                            icon="fas fa-book-open"
                                            size="small"
                                            :href="
                                                webroot +
                                                '/docs/Anleitung_TG_SH_Energiefoerderportal.pdf'
                                            "
                                            target="_blank"
                                            @click="
                                                onDownloadClick(
                                                    'Anleitung_TG_SH_Energiefoerderportal.pdf',
                                                )
                                            "
                                        >
                                        </v-btn>
                                    </template>
                                    <span>Anleitung Förderportal für TG und SH</span>
                                </v-tooltip>
                                <v-tooltip v-if="customer === 'tg'" bottom>
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            class="rounded"
                                            variant="outlined"
                                            v-bind="props"
                                            color="primary"
                                            size="small"
                                            :href="webroot + '/docs/Ablaufschema_Apollon_TG.pdf'"
                                            icon=""
                                            target="_blank"
                                            @click="onDownloadClick('Ablaufschema_Apollon_TG.pdf')"
                                        >
                                            <img
                                                :src="TgWappenSvg"
                                                width="18"
                                                alt="Wappen Thurgau"
                                            />
                                        </v-btn>
                                    </template>
                                    <span>Ablauf Fördergesuch TG</span>
                                </v-tooltip>
                                <v-tooltip v-if="customer === 'sh'" bottom>
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            class="rounded"
                                            variant="outlined"
                                            v-bind="props"
                                            color="primary"
                                            size="small"
                                            :href="webroot + '/docs/Ablaufschema_Apollon_SH.pdf'"
                                            icon=""
                                            target="_blank"
                                            @click="onDownloadClick('Ablaufschema_Apollon_SH.pdf')"
                                        >
                                            <img
                                                :src="ShWappenSvg"
                                                width="18"
                                                alt="Wappen Thurgau"
                                            />
                                        </v-btn>
                                    </template>
                                    <span>Ablauf Fördergesuch SH</span>
                                </v-tooltip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card>
                            <v-card-title>
                                {{ $t('vollmacht') }}
                            </v-card-title>
                            <v-card-text>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            class="rounded mr-2"
                                            variant="outlined"
                                            v-bind="props"
                                            color="primary"
                                            icon="far fa-file-word"
                                            size="small"
                                            :href="webroot + '/docs/Vorlage_Vollmacht.docx'"
                                            target="_blank"
                                            @click="onDownloadClick('Vorlage_Vollmacht.docx')"
                                        >
                                        </v-btn>
                                    </template>
                                    <span>Vollmacht-Vorlage als Word</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ props }">
                                        <v-btn
                                            class="rounded"
                                            variant="outlined"
                                            v-bind="props"
                                            color="primary"
                                            icon="far fa-file-pdf"
                                            size="small"
                                            :href="webroot + '/docs/Vorlage_Vollmacht.pdf'"
                                            target="_blank"
                                            @click="onDownloadClick('Vorlage_Vollmacht.pdf')"
                                        >
                                        </v-btn>
                                    </template>
                                    <span>Vollmacht-Vorlage als PDF</span>
                                </v-tooltip>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-card>
                            <v-card-title>
                                <v-avatar>
                                    <v-icon color="info text--white">fas fa-info-circle</v-icon>
                                </v-avatar>
                                {{ $t('allgemeine_infos') }}
                            </v-card-title>
                            <v-card-text class="black--text ml-5">
                                <ul>
                                    <li>
                                        Das Fördergesuch muss
                                        <strong>vor Bau- bzw. Installationsbeginn</strong>
                                        eingereicht werden. Nach der Einreichung des Fördergesuchs
                                        kann mit der Realisierung des Vorhabens begonnen werden,
                                        dies jedoch auf eigenes Risiko.
                                    </li>
                                    <li v-if="customer === 'tg'">
                                        Eine Förderzusage ist zwei Jahre gültig (Minergie-Neubauten,
                                        Wärmenetzprojekte: drei Jahre; GEAK mit Beratungsbericht,
                                        Machbarkeitsstudien, Energieanalysen: ein Jahr). Sie kann um
                                        ein Jahr verlängert werden. Beachten Sie die Funktion
                                        <em>Verlängerung beantragen</em>
                                        <img :src="webroot + '/btn_verlaengerung.png'" />
                                    </li>
                                    <li v-else>
                                        Eine Förderzusage ist zwei Jahre gültig (Minergie-Neubauten,
                                        Wärmenetzprojekte: drei Jahre; GEAK mit Beratungsbericht,
                                        Machbarkeitsstudien, Energieanalysen, Umstiegsprämie Kauf
                                        Elektrofahrzeug: ein Jahr). Sie kann um ein Jahr verlängert
                                        werden. Beachten Sie die Funktion
                                        <em>Verlängerung beantragen</em>
                                        <img :src="webroot + '/btn_verlaengerung.png'" />
                                    </li>
                                    <li>
                                        Eine Doppelförderung mit der Stiftung Klimaschutz und
                                        CO₂-Kompensation KliK oder mit Energie Zukunft Schweiz ist
                                        nicht zulässig. Unternehmensstandorte, die von der
                                        CO₂-Abgabe befreit sind, sind nicht förderberechtigt.
                                    </li>
                                    <li>
                                        Änderungen an den Fördersätzen und Förderbedingungen bleiben
                                        vorbehalten. Es gelten jeweils die aktuellen Fördersätze und
                                        Förderbedingungen zum Zeitpunkt der Gesuchseinreichung.
                                    </li>
                                    <li>
                                        Falls das Gesuch nicht von der Eigentümerschaft, sondern von
                                        einem Bevollmächtigten eingereicht wird, ist eine Vollmacht
                                        notwendig. Eine Vorlage finden Sie auf dieser Seite.
                                    </li>
                                    <li>
                                        Alle Informationen zu einem Gesuch werden von den
                                        beteiligten Organisationen und Fachleuten absolut
                                        vertraulich behandelt.
                                    </li>
                                    <li>
                                        Die Bearbeitungsstelle kann nicht für Schäden haftbar
                                        gemacht werden, welche bei der Planung, der Erstellung und
                                        dem Betrieb des geförderten Projekts entstehen können
                                    </li>
                                </ul>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <admin-tools v-if="benutzer?.rolle === 'admin'" />
            </v-container>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.user-email {
    font-size: 0.85em;
}

.rounded {
    border-radius: 50% !important;
}

.centered-container {
    margin: auto;
    max-width: 1000px;
}
</style>
